import React, { useEffect, useRef, useState } from "react";
import { SearchIcon, XIcon, BoxIcon } from "lucide-react";
import { FieldLabel } from "./Section";
export interface PillOption {
  id: string;
  primary: string;
  secondary?: string;
  tag?: string;
}
interface PillMultiSelectProps {
  id: string;
  label: string;
  placeholder: string;
  helpText?: string;
  options: PillOption[];
  selectedIds: string[];
  onChange: (ids: string[]) => void;
  pillIcon: BoxIcon;
  minChars?: number;
  required?: boolean;
  showInitials?: boolean;
}
export function PillMultiSelect({
  id,
  label,
  placeholder,
  helpText,
  options,
  selectedIds,
  onChange,
  pillIcon: PillIcon,
  minChars = 3,
  required,
  showInitials = true
}: PillMultiSelectProps) {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    const handle = (e: MouseEvent) => {
      if (!containerRef.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handle);
    return () => document.removeEventListener('mousedown', handle);
  }, []);
  const q = query.trim().toLowerCase();
  const meetsThreshold = q.length >= minChars;
  const results = meetsThreshold ? options.filter(o => !selectedIds.includes(o.id) && (o.primary.toLowerCase().includes(q) || (o.secondary ?? '').toLowerCase().includes(q))).slice(0, 8) : [];
  const selected = selectedIds.map(sid => options.find(o => o.id === sid)).filter((o): o is PillOption => Boolean(o));
  const showDropdown = open && (q.length > 0 || minChars === 0);
  const listboxId = `${id}-listbox`;
  const select = (option: PillOption) => {
    onChange([...selectedIds, option.id]);
    setQuery('');
    setActiveIndex(0);
    if (minChars > 0) setOpen(false);
    inputRef.current?.focus();
  };
  const remove = (optionId: string) => {
    onChange(selectedIds.filter(s => s !== optionId));
  };
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setOpen(true);
      setActiveIndex(i => Math.min(i + 1, Math.max(results.length - 1, 0)));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex(i => Math.max(i - 1, 0));
    } else if (e.key === 'Enter') {
      if (showDropdown && results[activeIndex]) {
        e.preventDefault();
        select(results[activeIndex]);
      }
    } else if (e.key === 'Escape') {
      setOpen(false);
    } else if (e.key === 'Backspace' && !query && selectedIds.length) {
      remove(selectedIds[selectedIds.length - 1]);
    }
  };
  return <div ref={containerRef} data-id="element-244" data-mp="2jk350wpuo5lw">
      <FieldLabel htmlFor={id} required={required} data-id="element-245" data-mp="2yf0yxu0nd353">
        {label}
      </FieldLabel>
      <div className="relative" data-id="element-246" data-mp="2tg66s1gsi28b">
        <SearchIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" aria-hidden="true" data-id="element-247" data-mp="2tgz6wjl8tthh" />
        <input ref={inputRef} id={id} type="text" role="combobox" autoComplete="off" aria-expanded={showDropdown} aria-controls={listboxId} aria-autocomplete="list" aria-activedescendant={showDropdown && results[activeIndex] ? `${id}-option-${results[activeIndex].id}` : undefined} value={query} onChange={e => {
        setQuery(e.target.value);
        setActiveIndex(0);
        setOpen(true);
      }} onFocus={() => setOpen(true)} onKeyDown={handleKeyDown} placeholder={placeholder} className="block w-full rounded-lg border border-gray-300 bg-white py-2.5 pl-9 pr-3 text-sm text-gray-900 placeholder-gray-400 transition-colors focus:border-navy-600 focus:outline-none focus:ring-1 focus:ring-navy-600" data-id="element-248" data-mp="3d2r7jusctsqk" />

        {showDropdown && <div className="absolute z-20 mt-1 w-full overflow-hidden rounded-lg border border-gray-200 bg-white shadow-lg" data-id="element-249" data-mp="2af4iatxsbvwc">
            {!meetsThreshold ? <p className="px-3 py-2.5 text-xs text-gray-500" data-id="element-250" data-mp="2fd625h31bz8v">
                Type {minChars - q.length} more character
                {minChars - q.length === 1 ? '' : 's'} to search
              </p> : results.length === 0 ? <p className="px-3 py-2.5 text-xs text-gray-500" data-id="element-251" data-mp="3dqk1wo3jc2gd">
                No matches for “{query.trim()}”
              </p> : <ul id={listboxId} role="listbox" className="max-h-64 overflow-y-auto py-1" data-id="element-252" data-mp="38x8rkczil3jp">
                {results.map((option, index) => <li key={option.id} id={`${id}-option-${option.id}`} role="option" aria-selected={index === activeIndex} onMouseDown={e => {
            e.preventDefault();
            select(option);
          }} onMouseEnter={() => setActiveIndex(index)} className={`flex cursor-pointer items-center gap-3 px-3 py-2 ${index === activeIndex ? 'bg-navy-50' : ''}`} data-id="element-253" data-mp="2z15qlagefayc" data-mpi={index}>
                    {showInitials && <span className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full bg-gray-100 text-[11px] font-semibold text-gray-600" data-id="element-254" data-mp="2l3pk5yy3rh6l">
                        {initials(option.primary)}
                      </span>}
                    <span className="min-w-0 flex-1" data-id="element-255" data-mp="quajozzglu6w">
                      <span className="block truncate text-sm font-medium text-gray-900" data-id="element-256" data-mp="qroeh701jqru">
                        {option.primary}
                      </span>
                      {option.secondary && <span className="block truncate text-xs text-gray-500" data-id="element-257" data-mp="10k3vo23ikbbo">
                          {option.secondary}
                        </span>}
                    </span>
                    {option.tag && <span className="flex-shrink-0 rounded bg-gray-100 px-1.5 py-0.5 text-[10px] font-medium text-gray-600" data-id="element-258" data-mp="7whhy10knqb3">
                        {option.tag}
                      </span>}
                  </li>)}
              </ul>}
          </div>}
      </div>

      {selected.length > 0 && <ul className="mt-2.5 flex flex-wrap gap-2" aria-label={`Selected for ${label}`} data-id="element-259" data-mp="msp1r082ippu">
          {selected.map(option => <li key={option.id} className="inline-flex max-w-full items-center gap-1.5 rounded-full border border-navy-100 bg-navy-50 py-1 pl-2.5 pr-1 text-xs font-medium text-navy-700" data-id="element-260" data-mp="1t7hopp43cmqi">
              <PillIcon className="h-3.5 w-3.5 flex-shrink-0" aria-hidden="true" data-id="element-261" data-mp="3u8xjqxqwkhab" />
              <span className="truncate" data-id="element-262" data-mp="3pa2t59mp1ofn">{option.primary}</span>
              <button type="button" onClick={() => remove(option.id)} aria-label={`Remove ${option.primary}`} className="flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full text-navy-500 transition-colors hover:bg-navy-100 hover:text-navy-800 focus:outline-none focus-visible:ring-2 focus-visible:ring-navy-600" data-id="element-263" data-mp="75j0iq3lz8lv">
                <XIcon className="h-3 w-3" data-id="element-264" data-mp="36lfr6zztdf6a" />
              </button>
            </li>)}
        </ul>}

      {helpText && <p className="mt-2 text-xs text-gray-500" data-id="element-265" data-mp="3kd2wfpbqhy17">{helpText}</p>}
    </div>;
}
function initials(name: string) {
  return name.split(' ').map(part => part.charAt(0)).slice(0, 2).join('').toUpperCase();
}